import React, { useMemo, useState } from 'react';
import type {
  ExercisePrePracticeReadModel,
  PrePracticeLevel,
  TierState,
} from 'Containers/ExerciseSection/prePracticeReadModel';
import { tierProgressKey } from 'Containers/ExerciseSection/prePracticeReadModel';
import { shownTonic } from 'Seeds/ExerciseContent/writtenCellName';

/**
 * Pre-practice overview for the Exercises section (MUS-312).
 *
 * This is the exercise OVERVIEW: it orients the learner and shows the whole
 * shape of the work (the level ladder and each level's Beginner / Intermediate
 * / Advanced tiers) plus how far their grip reaches. It carries no Start - a
 * level is a tap target, and the per-level pre-session surface (with the play
 * action) lands after the Phase 1 stop gate. Everything here is derived from
 * territory (#124): structure from the read model, grip from its
 * optional progress slice, which is absent in the skeleton so every tier reads
 * untouched (the honest day-one state). Props-typed on the read model per
 * Decision #131.1; it does not know a fixture exists.
 */

interface PrePracticeScreenProps {
  readModel: ExercisePrePracticeReadModel;
  /**
   * Tap-to-play seam (MUS-481). When provided, each tier row in an expanded level
   * becomes a play target: tapping it enters the play scene for the key chosen in
   * that level (its first cell until another is chosen) at the tapped tier's
   * waypoint. Omitted in contexts with no navigation (the screen stays a pure
   * readout, as before), so this is additive.
   */
  onSelectLevel?: (levelNumber: number, tierNumber?: number, tonic?: string) => void;
}

const TIER_LABELS: Record<number, string> = {
  1: 'Beginner',
  2: 'Intermediate',
  3: 'Advanced',
};

/** A tier is "cleared enough to count" once it is cleared or mastered. */
function isClearedState(state: TierState): boolean {
  return state === 'cleared' || state === 'mastered';
}

/** The one glyph that carries a tier's grip state, with a text equivalent. */
const TierGlyph: React.FC<{ state: TierState; size?: number }> = ({ state, size = 12 }) => {
  const label = state;
  if (state === 'mastered') {
    return (
      <span className="epp-glyph epp-glyph-mastered" role="img" aria-label={label}>
        <svg viewBox="0 0 24 24" width={size + 2} height={size + 2} aria-hidden="true">
          <path
            d="M12 2.5l2.9 5.88 6.49.94-4.7 4.58 1.11 6.46L12 17.3l-5.8 3.05 1.1-6.46-4.7-4.58 6.5-.94z"
            fill="currentColor"
          />
        </svg>
      </span>
    );
  }
  return (
    <span
      className={`epp-glyph epp-glyph-${state}`}
      role="img"
      aria-label={label}
      style={{ width: size, height: size }}
    >
      {state === 'attempted' && <span className="epp-glyph-core" />}
    </span>
  );
};

const PrePracticeScreen: React.FC<PrePracticeScreenProps> = ({ readModel, onSelectLevel }) => {
  const { title, description, classification, levels, progress } = readModel;
  const [expandedLevel, setExpandedLevel] = useState<number | null>(null);
  // The key chosen in each level (by level number); a level's first cell until one is chosen.
  const [chosenTonics, setChosenTonics] = useState<Record<number, string>>({});
  const tonicFor = (level: PrePracticeLevel): string | undefined =>
    chosenTonics[level.levelNumber] ?? level.cells[0]?.tonic;

  const stateOf = useMemo(
    () =>
      (levelNumber: number, tierNumber: number): TierState =>
        progress?.[tierProgressKey(levelNumber, tierNumber)] ?? 'untouched',
    [progress],
  );

  /** A level counts as cleared once its Beginner tier is cleared-or-better. */
  const isLevelCleared = (level: PrePracticeLevel): boolean => {
    const beginner = level.tiers.find((t) => t.tierNumber === 1) ?? level.tiers[0];
    return beginner ? isClearedState(stateOf(level.levelNumber, beginner.tierNumber)) : false;
  };

  const clearedCount = levels.filter(isLevelCleared).length;

  /** Suggested-next = the first level, in display order, not yet cleared; a signal, not a lock. */
  const suggestedLevel = useMemo(() => {
    return levels.find((lv) => !isLevelCleared(lv)) ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levels, progress]);

  const approach = classification.approachLabel.replace(/^by\s+/i, '');

  return (
    <main className="epp" aria-labelledby="epp-title">
      <div className="epp-panel epp-orient">
        <div className="epp-identity">
          <span className="epp-topic-icon" aria-hidden="true">
            <ApproachIcon approach={approach} />
          </span>
          <div className="epp-identity-labels">
            <span className="epp-topic">{classification.topicLabel}</span>
            <span className="epp-approach">{approach}</span>
          </div>
        </div>

        <h1 className="epp-title" id="epp-title">
          {title}
        </h1>
        {description && <p className="epp-subtitle">{description}</p>}

        <section className="epp-progress" aria-label="Progress">
          <div className="epp-progress-head">
            <span className="epp-eyebrow">Progress</span>
            <span className="epp-progress-count">
              {clearedCount} of {levels.length} levels cleared
            </span>
          </div>
          <div className="epp-progress-grid" aria-hidden="true">
            {levels.map((level) => (
              <div className="epp-progress-col" key={level.levelNumber}>
                {[...level.tiers]
                  .sort((a, b) => a.tierNumber - b.tierNumber)
                  .map((tier) => (
                    <span
                      key={tier.tierNumber}
                      className={`epp-progress-cell epp-fill-${stateOf(
                        level.levelNumber,
                        tier.tierNumber,
                      )}`}
                    />
                  ))}
              </div>
            ))}
          </div>
          {suggestedLevel && (
            <p className="epp-pickup">
              Pick up here
              <span className="epp-pickup-target">{suggestedLevel.title}</span>
            </p>
          )}
        </section>
      </div>

      <div className="epp-panel epp-ladder-panel">
        <span className="epp-eyebrow epp-ladder-eyebrow">Levels</span>
        <ol className="epp-ladder">
          {levels.map((level, index, ordered) => {
            const prevReview = index > 0 && !ordered[index - 1].isReview && level.isReview;
            const isSuggested = suggestedLevel?.levelNumber === level.levelNumber;
            const isExpanded = expandedLevel === level.levelNumber;
            return (
              <li key={level.levelNumber} className="epp-ladder-item">
                {prevReview && (
                  <div className="epp-review-divider" aria-hidden="true">
                    <span>Review</span>
                    <span className="epp-review-rule" />
                  </div>
                )}
                <div
                  className={
                    'epp-level' +
                    (isSuggested ? ' is-suggested' : '') +
                    (isExpanded ? ' is-expanded' : '')
                  }
                >
                  <button
                    type="button"
                    className="epp-level-head"
                    aria-expanded={isExpanded}
                    onClick={() => setExpandedLevel(isExpanded ? null : level.levelNumber)}
                  >
                    <span className="epp-level-num">
                      {String(level.levelNumber).padStart(2, '0')}
                    </span>
                    <span className="epp-level-body">
                      <span className="epp-level-title">{level.title}</span>
                      {level.description && (
                        <span className="epp-level-desc">{level.description}</span>
                      )}
                    </span>
                    {isSuggested && <span className="epp-pickup-flag">Pick up here</span>}
                    <span className="epp-level-glyphs">
                      {[...level.tiers]
                        .sort((a, b) => a.tierNumber - b.tierNumber)
                        .map((tier) => (
                          <TierGlyph
                            key={tier.tierNumber}
                            state={stateOf(level.levelNumber, tier.tierNumber)}
                          />
                        ))}
                    </span>
                  </button>
                  {isExpanded && onSelectLevel && level.cells.length > 1 && (
                    <label className="epp-key-choice">
                      <span className="epp-eyebrow">Key</span>
                      <select
                        value={tonicFor(level)}
                        aria-label={`${level.title} key`}
                        onChange={(event) =>
                          setChosenTonics((chosen) => ({
                            ...chosen,
                            [level.levelNumber]: event.target.value,
                          }))
                        }
                      >
                        {level.cells.map((cell) => (
                          <option key={cell.tonic} value={cell.tonic}>
                            {shownTonic(cell)}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {isExpanded && (
                    <ul className="epp-tiers" aria-label={`${level.title} tiers`}>
                      {[...level.tiers]
                        .sort((a, b) => a.tierNumber - b.tierNumber)
                        .map((tier) => {
                          const tierLabel =
                            TIER_LABELS[tier.tierNumber] ?? `Tier ${tier.tierNumber}`;
                          const tierContent = (
                            <>
                              <TierGlyph
                                state={stateOf(level.levelNumber, tier.tierNumber)}
                                size={11}
                              />
                              <span className="epp-tier-name">{tierLabel}</span>
                              <span className="epp-tier-tempo">
                                {'\u2669'} {tier.tempo}
                              </span>
                            </>
                          );
                          return (
                            <li
                              className={'epp-tier' + (onSelectLevel ? ' epp-tier--tap' : '')}
                              key={tier.tierNumber}
                            >
                              {onSelectLevel ? (
                                <button
                                  type="button"
                                  className="epp-tier-play"
                                  aria-label={`Play ${level.title}, ${tierLabel}`}
                                  onClick={() =>
                                    onSelectLevel(
                                      level.levelNumber,
                                      tier.tierNumber,
                                      tonicFor(level),
                                    )
                                  }
                                >
                                  {tierContent}
                                </button>
                              ) : (
                                tierContent
                              )}
                            </li>
                          );
                        })}
                    </ul>
                  )}
                </div>
              </li>
            );
          })}
        </ol>

        <div className="epp-legend" aria-hidden="true">
          <span className="epp-legend-item">
            <TierGlyph state="untouched" size={11} /> untouched
          </span>
          <span className="epp-legend-item">
            <TierGlyph state="attempted" size={11} /> attempted
          </span>
          <span className="epp-legend-item">
            <TierGlyph state="cleared" size={11} /> cleared
          </span>
          <span className="epp-legend-item">
            <TierGlyph state="mastered" size={11} /> mastered
          </span>
        </div>
      </div>
    </main>
  );
};

/**
 * Per-exercise visual identity keyed off the classification, not bespoke art
 * (scales to the whole catalog). Only "playing" ships in the slice; the other
 * approaches are the seam where the classification-keyed icon set plugs in.
 */
const ApproachIcon: React.FC<{ approach: string }> = ({ approach }) => {
  if (/play/i.test(approach)) {
    return (
      <svg viewBox="0 0 24 24" width="21" height="21" aria-hidden="true">
        <rect
          x="2.5"
          y="5"
          width="19"
          height="14"
          rx="1.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
        />
        <line x1="8.8" y1="5" x2="8.8" y2="19" stroke="currentColor" strokeWidth="1.2" />
        <line x1="15.2" y1="5" x2="15.2" y2="19" stroke="currentColor" strokeWidth="1.2" />
        <rect x="6.4" y="5" width="3" height="8" fill="currentColor" />
        <rect x="12.8" y="5" width="3" height="8" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" width="21" height="21" aria-hidden="true">
      <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
};

export default PrePracticeScreen;
